'use client';

import {
	BigNum,
	MarketType,
	PRICE_PRECISION_EXP,
} from '@velocity-exchange/sdk';
import { MarketId } from '@velocity-exchange/common';
import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
	usePricingData,
	PRICING_QUERY_KEY,
} from './query/pricing/usePricingData';
import { PricingData } from './query/pricing/types';

const DEFAULT_VALUE = BigNum.zero(PRICE_PRECISION_EXP);

/**
 * Converts PricingData to a BigNum mark price.
 * Falls back to oracle price if mark price is 0.
 */
export const pricingDataToMarkPrice = (
	pricingData: PricingData | undefined
): BigNum => {
	if (!pricingData) return DEFAULT_VALUE;
	const price =
		pricingData.markPrice > 0 ? pricingData.markPrice : pricingData.oraclePrice;
	return BigNum.fromPrint(price.toString(), PRICE_PRECISION_EXP);
};

const useMarkPrice = (marketId?: MarketId): BigNum => {
	const marketKey = useMemo(() => {
		const marketIndex = marketId?.marketIndex ?? 0;
		const marketType = marketId?.marketType ?? MarketType.PERP;

		return new MarketId(marketIndex, marketType).key;
	}, [marketId?.marketIndex, marketId?.marketType]);

	const { data: pricingData } = usePricingData(marketKey);

	return pricingDataToMarkPrice(pricingData);
};

/**
 * Returns a function that gets the mark price for any market from the React Query pricing cache.
 * Use this in list components where you can't call useMarkPrice per-market.
 */
export const useGetMarkPriceForMarket = () => {
	const queryClient = useQueryClient();

	return useCallback(
		(marketId: MarketId): BigNum => {
			const allPricing =
				queryClient.getQueryData<Map<string, PricingData>>(PRICING_QUERY_KEY);
			return pricingDataToMarkPrice(allPricing?.get(marketId.key));
		},
		[queryClient]
	);
};

export default useMarkPrice;
